import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

/**
 * Sync schedule status from API
 */
export interface SyncScheduleStatus {
  id: string;
  name: string;
  enabled: boolean;
  syncType: 'full' | 'incremental';
  intervalMinutes: number;
  lastRunAt: string | null;
  nextRunAt: string | null;
  lastRunStatus: 'success' | 'failed' | 'running' | null;
  lastRunMessage: string | null;
  lastRunDevicesProcessed: number | null;
  lastRunDurationSeconds: number | null;
  isDue: boolean;
  isRunning: boolean;
}

/**
 * Sync schedule update request
 */
export interface SyncScheduleUpdateRequest {
  enabled: boolean;
  intervalMinutes: number;
  syncType: 'full' | 'incremental';
}

/**
 * Trigger sync response
 */
export interface TriggerSyncResponse {
  success: boolean;
  message: string;
  devicesProcessed?: number;
  durationSeconds?: number;
  error?: string;
}

/**
 * Hook to fetch sync schedule status
 */
export function useSyncSchedule() {
  return useQuery({
    queryKey: ['sync', 'schedule'],
    queryFn: async () => {
      return api.get<SyncScheduleStatus>('/api/v1/sync/schedule');
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

/**
 * Hook to update sync schedule settings
 */
export function useUpdateSyncSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SyncScheduleUpdateRequest) => {
      return api.put<{ success: boolean; message: string; schedule: SyncScheduleStatus }>(
        '/api/v1/sync/schedule',
        data
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sync', 'schedule'] });
    },
  });
}

/**
 * Hook to trigger an immediate sync
 */
export function useTriggerSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return api.post<TriggerSyncResponse>('/api/v1/sync/trigger');
    },
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['sync', 'schedule'] });
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });
}

/**
 * Hook to fetch sync history
 */
export function useSyncHistory() {
  return useQuery({
    queryKey: ['sync', 'history'],
    queryFn: async () => {
      return api.get<{
        history: Array<{
          runAt: string;
          status: string;
          message: string | null;
          devicesProcessed: number | null;
          durationSeconds: number | null;
        }>;
        total: number;
      }>('/api/v1/sync/history');
    },
  });
}
